/**
 * IS THE CHAIN UP — the backend's answer to `GET /blockchain-status`, read in one place.
 *
 * The answer is `data.available`, never the HTTP status: a chain in maintenance still answers
 * 200. The words to show come from the backend (`data.message.fa` / `.en`), never from here.
 * Anything that is not a clear `available: true` — a failed request, a body of another shape —
 * reads as down, so nobody sends a transaction onto a broken chain.
 */

export type ChainMessage = { fa: string; en: string };
export type ChainStatus = { available: boolean; message: ChainMessage | null };

const text = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);

export function readBlockchainStatus(body: unknown): ChainStatus {
  const root = body && typeof body === "object" ? (body as Record<string, unknown>) : null;
  const data = root?.data && typeof root.data === "object" ? (root.data as Record<string, unknown>) : null;
  const raw = data?.message && typeof data.message === "object" ? (data.message as Record<string, unknown>) : null;
  const fa = text(raw?.fa), en = text(raw?.en);
  // One language missing: the other stands in, then the top-level message (sent in the request's language).
  const top = text(root?.message);
  const message = fa || en || top ? { fa: fa ?? en ?? top ?? "", en: en ?? fa ?? top ?? "" } : null;
  return { available: data?.available === true, message };
}

/** The status a failed request stands for: down, with nothing from the backend to say. */
export const UNREACHABLE: ChainStatus = { available: false, message: null };

/** The backend caches the answer for five minutes; asking more often gains nothing. */
export const RECHECK_MS = 5 * 60_000;
